import React, { useCallback, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";
import { ATTENDANCE_BRANCHES } from "../../config/attendanceBranches";
import { getVisibleAttendanceBranches } from "../../config/appBranches";
import { loadMonthlyAttendanceRows, formatPresentDayUnits } from "../../helpers/payrollAttendanceCore";
import {
  bucketDayUnitsByCalendarWeeks,
  bucketDayUnitsByFortnight,
} from "../../helpers/payrollPeriodBuckets";

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

export default function PayrollPeriodAttendanceReport() {
  const storedUser = useMemo(() => getStoredUser(), []);
  const visibleBranches = useMemo(() => getVisibleAttendanceBranches(storedUser), [storedUser]);
  const branchPool = visibleBranches.length ? visibleBranches : ATTENDANCE_BRANCHES;

  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [shopLabel, setShopLabel] = useState(branchPool[0]?.label || ATTENDANCE_BRANCHES[0].label);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const currentShop = useMemo(
    () => branchPool.find((b) => b.label === shopLabel) || branchPool[0],
    [branchPool, shopLabel]
  );

  const shiftMonth = (delta) => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));

  const load = useCallback(async () => {
    if (!currentShop) return;
    setLoading(true);
    try {
      const { rows: raw } = await loadMonthlyAttendanceRows(currentShop, month);
      const enriched = raw.map((r) => {
        const units =
          r.presentUnitsByDay && Object.keys(r.presentUnitsByDay).length > 0
            ? r.presentUnitsByDay
            : Object.fromEntries((r.presentDates || []).map((d) => [d, 1]));
        const w = bucketDayUnitsByCalendarWeeks(units);
        const f = bucketDayUnitsByFortnight(units);
        return {
          ...r,
          ...w,
          ...f,
        };
      });
      setRows(enriched);
      toast.success("Loaded");
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed");
    } finally {
      setLoading(false);
    }
  }, [currentShop, month]);

  return (
    <div className="min-h-screen bg-slate-100/90">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto max-w-[90rem] px-4 py-6">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">Payroll · Reports</p>
          <h1 className="text-2xl font-bold text-slate-900">Weekly & fortnightly attendance</h1>
          <p className="mt-1 text-sm text-slate-600">
            Present-day counts split by <strong>calendar weeks</strong> (weeks start on the 1st: days 1–7, 8–14, …) and
            <strong> fortnights</strong> (1–15 vs 16–end). Same punch data as Monitor.
          </p>
        </div>

        <div className="mt-6 flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2 py-1 shadow-sm">
            <button type="button" onClick={() => shiftMonth(-1)} className="rounded-lg p-1 hover:bg-slate-100">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="min-w-[10rem] text-center text-sm font-semibold">
              {month.toLocaleString("en-IN", { month: "long", year: "numeric" })}
            </span>
            <button type="button" onClick={() => shiftMonth(1)} className="rounded-lg p-1 hover:bg-slate-100">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          <label className="text-xs font-semibold text-slate-700">
            Branch
            <select
              value={shopLabel}
              onChange={(e) => setShopLabel(e.target.value)}
              className="mt-1 block min-w-[10rem] rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
            >
              {branchPool.map((b) => (
                <option key={b.label} value={b.label}>
                  {b.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white shadow hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? "Loading…" : "Load"}
          </button>
        </div>

        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-left text-[11px]">
            <thead className="bg-slate-800 text-[9px] font-bold uppercase tracking-wide text-slate-100">
              <tr>
                <th className="sticky left-0 z-10 bg-slate-800 px-2 py-2">Employee</th>
                <th className="px-1 py-2 text-right">Total</th>
                <th className="px-1 py-2 text-right">W1</th>
                <th className="px-1 py-2 text-right">W2</th>
                <th className="px-1 py-2 text-right">W3</th>
                <th className="px-1 py-2 text-right">W4</th>
                <th className="px-1 py-2 text-right">W5</th>
                <th className="px-1 py-2 text-right">F1</th>
                <th className="px-1 py-2 text-right">F2</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-slate-500">
                    Click Load.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.employeeId} className="border-t border-slate-100">
                    <td className="sticky left-0 z-[1] bg-white px-2 py-1">
                      <div className="whitespace-nowrap font-medium">{r.employeeName}</div>
                    </td>
                    <td className="px-1 py-1 text-right tabular-nums">{formatPresentDayUnits(r.presentDays)}</td>
                    <td className="px-1 py-1 text-right tabular-nums">{r.W1}</td>
                    <td className="px-1 py-1 text-right tabular-nums">{r.W2}</td>
                    <td className="px-1 py-1 text-right tabular-nums">{r.W3}</td>
                    <td className="px-1 py-1 text-right tabular-nums">{r.W4}</td>
                    <td className="px-1 py-1 text-right tabular-nums">{r.W5}</td>
                    <td className="px-1 py-1 text-right tabular-nums">{r.F1_1_to_15}</td>
                    <td className="px-1 py-1 text-right tabular-nums">{r.F2_16_to_end}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
